<template>
  <div class="app-content editing clearfix">
    <template v-if="loaded">
      <pointer-effect />
      <header-bar
        :show-teams="false"
        :show-save="true"
        :show-back="true"
        :show-shortcuts="true"
        :has-unsaved-changes="hasUnsavedChanges"
        :disable-auto-save="disableAutoSave"
        data-test="graphics-headerBar"
        @save="save"
        @enable-auto-save="disableAutoSave = false"
        @auto-save="save({toast: false, autoSave: true})"
        @modal="togglekeyboardShortcuts"
      />
      <div class="builder-main-content">
        <sidebar-left
          ref="sidebarLeft"
          :template="template"
          v-model:show-element-library="elementLibraryOpen"
          @modal="toggleAssetModal"
          @open-social-modal="showSocialModal = true"
          @open-sportradar-modal="openSportradarModal"
          @open-shoppable-modal="showShoppableModal = true"
          @open-scorestream-modal="showScorestreamModal = true"
          @open-foxforesight-modal="showFoxForesightModal = true"
          @open-google-sheets-modal="openGoogleSheetsModal"
          @preview="openPresetPreview"
        />

        <template-preview
          ref="prvTmp"
          :show-element-library="elementLibraryOpen"
        />

        <sidebar-right
          :template="template"
          @open-google-sheets-modal="openGoogleSheetsModal"
          @modal="toggleAssetModal"
        />
      </div>
    </template>

    <!--TODO:
    While fixing some things for VUEX review, found this bit of code that doesn't
    fires on production either. Seems like something we should fix. -->
    <alert
      v-if="showAlert"
      data-test="graphics-alertUnsaved"
      @confirm="navigateHome"
      @close="showAlert = false"
    >
      <p>You have unsaved changes.</p>
      <p>
        Are you sure you want to exit the template builder? Any unsaved changes
        will be lost.
      </p>
    </alert>

    <modal
      v-if="assetUploaderOpen"
      id="asset-uploader-modal"
      data-test="graphics-assetUploader"
      :header="false"
      :footer="false"
    >
      <template #modal-body>
        <iframe
          id="asset-uploader"
          :src="assetManagerSrc"
          data-test="graphics-assetUploaderIframe"
          height="100%"
          width="100%"
          style="width: 100%; height: 100%; border: 0"
        />
        <button
          v-if="assetUploaderOpen"
          class="modal-close-alt"
          data-test="graphics-assetUploaderClose"
          data-entity="graphics-assetUploaderClose"
          @click="assetUploaderOpen = false"
        >
          <span class="fa-solid fa-xmark" />
        </button>
      </template>
    </modal>

    <modal
      v-if="showFontManager"
      id="font-uploader-modal"
      data-test="graphics-fontUploader"
    >
      <template #modal-body>
        <iframe
          id="asset-uploader"
          :src="fontManagerSrc"
          data-test="graphics-font-uploader-iframe"
          height="100%"
          width="100%"
          style="width: 100%; height: 100%; border: 0"
        />

        <button
          class="modal-close-alt"
          data-test="graphics-fontUploaderClose"
          data-entity="graphics-fontUploaderClose"
          @click="closeModal()"
        >
          <span class="fa-solid fa-xmark" />
        </button>
      </template>
    </modal>

    <social-modal
      v-if="showSocialModal"
      :social-template="socialTemplate"
      action="createFullScreenSocial"
      @close="showSocialModal = false"
      @template-selections="handleSocialTemplateSelections"
    />

    <sportradar-connect-modal
      v-if="showSportradarModal"
      :league-name="activeSportradarLeague"
      @close="showSportradarModal = false"
    />

    <shoppable-modal
      v-if="showShoppableModal"
      @close="showShoppableModal = false"
      @select="handleShoppableSelect"
    />

    <scorestream-modal
      v-if="showScorestreamModal"
      @close="showScorestreamModal = false"
      @connect="handleScorestreamConnect"
    />

    <foxforesight-modal
      v-if="showFoxForesightModal"
      @close="showFoxForesightModal = false"
      @connect="handleFoxForesightConnect"
    />

    <google-connect-modal
      v-if="showGoogleSheetsModal && googleDataElement && canvas"
      :element="googleDataElement"
      :canvas="canvas"
      @close="closeGoogleSheetsModal"
    />

    <missing-tracking-link-modal
      v-if="showTrackingLinkWarning"
      :element-count="unlinkedQrCodeElements.length"
      @close="closeTrackingLinkWarning"
      @select="selectUnlinkedQrCode"
      @save="confirmSaveIgnoringTrackingLinkWarning"
    />

    <keyboard-shortcuts
      v-if="keyboardShortcutsOpen"
      @modal="togglekeyboardShortcuts"
    />
    <footer-bar @fit="handleFit()" />
    <modal
      v-if="showPresetPreview"
      :header="true"
      :footer="false"
      :close-button="true"
      auto-height
      data-test="graphics-presetPreviewModal"
      @close="closePresetPreview"
    >
      <template #modal-header>
        <h2 class="preset-preview__header">
          {{ previewPreset?.title }}
        </h2>
      </template>

      <template #modal-body>
        <div class="preset-preview__body">
          <img
            v-if="getPresetPreviewUrl(previewPreset)"
            :src="getPresetPreviewUrl(previewPreset)"
            :alt="previewPreset.title"
            class="preset-preview__image"
          >
        </div>
      </template>
    </modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import AppMixins from '../mixins/app-mixins';
import toast from '../utils/toasts';
import { inIframe } from '../utils/helpers';
import { allContentTypes } from '../utils/social';
import { findQrCodesMissingTrackingLink } from '../utils/qrCodeTrackingLinks';

import KeyboardShortcutsComponent from './KeyboardShortcuts.vue';
import FooterBar from './TemplateFooter.vue';
import SocialModal from './Modal/SocialModal.vue';
import SportradarConnectModal from './SidebarLeft/Sportradar/SportradarConnectModal.vue';
import ShoppableModal from './Modal/ShoppableModal.vue';
import ScorestreamModal from './Modal/ScorestreamModal.vue';
import FoxForesightModal from './Modal/FoxForesightModal.vue';
import GoogleConnectModal from './Modal/GoogleGonnectModal.vue';
import Modal from './Modal/Modal.vue';
import MissingTrackingLinkModal from './MissingTrackingLinkModal.vue';

export default {
  name: 'Edit',
  components: {
    'keyboard-shortcuts': KeyboardShortcutsComponent,
    FooterBar,
    SocialModal,
    SportradarConnectModal,
    ShoppableModal,
    ScorestreamModal,
    'foxforesight-modal': FoxForesightModal,
    GoogleConnectModal,
    Modal,
    MissingTrackingLinkModal,
  },
  mixins: [AppMixins],

  provide() {
    return {
      toggleFontModal: () => {
        this.showFontManager = !this.showFontManager;
      },
    };
  },
  beforeRouteEnter(to, from, next) {
    // 'from' is the route you're navigating away from
    // 'to' is the route you're navigating to
    const { template_id: templateId } = to.params;
    if (from.name === 'create') {
      next((vm) => {
        // eslint-disable-next-line no-param-reassign
        vm.loaded = false;
        vm.$store.dispatch('graphicsTemplate/setTemplateId', {
          value: templateId,
          options: {
            blacklist: true,
          },
        });
        // eslint-disable-next-line no-param-reassign
        vm.loaded = true;
      });
    } else {
      next((vm) => { // the `next` callback function can also accept the Vue instance
        // eslint-disable-next-line no-param-reassign
        vm.loaded = false;
        vm.$store.dispatch('getTemplate', templateId).then((template) => {
          // eslint-disable-next-line no-param-reassign
          vm.loaded = true;

          // new store value set
          vm.$store.dispatch('graphicsTemplate/setAllGraphicsTemplateState', {
            fullTemplate: template,
            options: { blacklist: true }, // SHOULD THIS ALWAYS BE BAKED INTO THE ACTION?
          }).then(() => {
            vm.$store.setInitialState();
          });
        });
      });
    }
  },
  data() {
    return {
      showFontManager: false,
      disableAutoSave: false,
      elementLibraryOpen: false,
      showPresetPreview: false,
      previewPreset: null,
      showSocialModal: false,
      socialTemplate: {
        template: 'fullScreen',
        options: allContentTypes,
      },
      showSportradarModal: false,
      showShoppableModal: false,
      showScorestreamModal: false,
      showFoxForesightModal: false,
      showGoogleSheetsModal: false,
      activeSportradarLeague: null,
      showTrackingLinkWarning: false,
      // QR code elements missing a trackingLinkRid, captured when a save is
      // intercepted so the modal can offer to select one.
      unlinkedQrCodeElements: [],
      // Save options to replay if the user chooses to save anyway.
      pendingSaveOptions: null,
    };
  },
  computed: {
    ...mapGetters({
      currentIndex: 'getCurrentIndex',
      currentSavedStateIndex: 'getCurrentSavedStateIndex',
    }),

    ...mapGetters('graphicsTemplate', [
      'getCanvas',
      'getElementById',
    ]),

    canvas() {
      return this.getCanvas;
    },

    googleDataElement() {
      const selectedIds = (
        this.$store.state.templates && this.$store.state.templates.selectedElements
      ) || [];

      if (!selectedIds.length) return null;

      const el = this.getElementById(selectedIds[0]);
      if (!el || el.type !== 'sheet-data') return null;

      return el;
    },

    user() {
      return this.$store.state.user;
    },

    hasUnsavedChanges() {
      return this.currentIndex !== this.currentSavedStateIndex;
    },

    // QR code elements that won't be attributed because they have no
    // TrackingLink selected. Data-mapped QR codes are excluded by the helper.
    qrCodesMissingTrackingLink() {
      const elementsById = this.$store.state.graphicsTemplate?.elements?.elementsById || {};
      const isMapped = this.$store.getters['graphicsTemplate/isPropertyMapped'];

      return findQrCodesMissingTrackingLink(elementsById, isMapped);
    },
  },

  async created() {
    await this.$store.dispatch('getPartners'); // Preload available partners and tokens

    // need this loaded prop to turn true for hot module replacement
    // because the logic to flip it happens in the beforeRouteEnter
    this.loaded = true;
  },

  methods: {
    ...mapActions(['setCurrentSavedStateIndex']),

    closeModal() {
      this.showFontManager = false;
      this.$store.dispatch('getFonts');
    },

    handleSocialTemplateSelections(payload) {
      if (this.$refs.sidebarLeft && this.$refs.sidebarLeft.createSocialElement) {
        this.$refs.sidebarLeft.createSocialElement(payload);
      }
      this.showSocialModal = false;
    },

    openSportradarModal({ leagueName }) {
      this.activeSportradarLeague = leagueName || null;
      this.showSportradarModal = true;
    },

    handleShoppableSelect(selectedElements) {
      if (this.$refs.sidebarLeft && this.$refs.sidebarLeft.addShoppableElement) {
        this.$refs.sidebarLeft.addShoppableElement(selectedElements);
      }

      this.showShoppableModal = false;
    },

    handleScorestreamConnect(config) {
      if (this.$refs.sidebarLeft && this.$refs.sidebarLeft.addScorestreamElement) {
        this.$refs.sidebarLeft.addScorestreamElement(config);
      }

      this.showScorestreamModal = false;
    },

    handleFoxForesightConnect(config) {
      if (this.$refs.sidebarLeft && this.$refs.sidebarLeft.addFoxForesightElement) {
        this.$refs.sidebarLeft.addFoxForesightElement(config);
      }

      this.showFoxForesightModal = false;
    },

    getPresetPreviewUrl(preset) {
      return preset?.posterUrl || preset?.thumbnailUrl || preset?.imageUrl || preset?.preview_url || '';
    },

    openPresetPreview(preset) {
      this.previewPreset = preset;
      this.showPresetPreview = true;
    },

    closePresetPreview() {
      this.showPresetPreview = false;
      this.previewPreset = null;
    },

    openGoogleSheetsModal() {
      this.showGoogleSheetsModal = true;
    },

    closeGoogleSheetsModal() {
      this.showGoogleSheetsModal = false;
    },

    async save(options = { toast: true, autoSave: false }) {
      const {
        toast: showToast,
        autoSave,
        bypassTrackingLinkWarning = false,
      } = options;

      // Auto-save runs in the background, so only intercept explicit saves.
      if (!autoSave && !bypassTrackingLinkWarning && this.qrCodesMissingTrackingLink.length) {
        this.unlinkedQrCodeElements = this.qrCodesMissingTrackingLink;
        this.pendingSaveOptions = options;
        this.showTrackingLinkWarning = true;
        return;
      }

      const template = await this.$store.dispatch('graphicsTemplate/transformGraphicsTemplateStateForSave');

      try {
        await this.$store.dispatch('editTemplate', template);
      } catch (err) {
        if (autoSave) {
          this.disableAutoSave = true;
          toast.makeToast({
            heading: 'Error',
            icon: 'error',
            text: 'There was an error saving your template. Autosave has been disabled to prevent error spamming.',
          });
          return;
        }

        toast.makeToast({
          heading: 'Error',
          icon: 'error',
          text: 'There was an error saving your template. Please try again.',
        });

        return;
      }

      if (showToast) {
        // Emit message out to any parents (if iframed).
        // That way the parent can handle routing (and toasts) instead.
        if (inIframe()) {
          try {
            window.parent.postMessage(`tgbGraphics:saved:${template._id}`, '*');
          } catch (err) {
            // If failed to post message to parent, then use existing toast.
            toast.makeToast({ text: 'Template saved successfully!' });
          }
        } else {
          // Not in an iframe, so use existing toast
          toast.makeToast({ text: 'Template saved successfully!' });
        }
      }

      this.setCurrentSavedStateIndex(this.currentIndex);
    },

    closeTrackingLinkWarning() {
      this.showTrackingLinkWarning = false;
      this.unlinkedQrCodeElements = [];
      this.pendingSaveOptions = null;
    },

    selectUnlinkedQrCode() {
      const [element] = this.unlinkedQrCodeElements;

      this.closeTrackingLinkWarning();

      if (element) {
        this.$store.dispatch('selectElements', [element.id]);
      }
    },

    confirmSaveIgnoringTrackingLinkWarning() {
      const options = this.pendingSaveOptions || {};

      this.closeTrackingLinkWarning();

      this.save({ ...options, bypassTrackingLinkWarning: true });
    },

    handleFit() {
      this.$refs.prvTmp.centerContent();
      this.$nextTick(() => {
        this.$refs.prvTmp.resize();
      });
    },
  },
};
</script>
<style lang="scss">
  .preset-preview__body {
  display: flex;
  justify-content: center;
  align-items: center;
}

.preset-preview__image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
}

.preset-preview__header {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}
</style>
